import { fn } from '@ember/helper';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import {
  PrismBanner,
  PrismBreadcrumb,
  PrismButton,
  PrismCard,
  PrismIcon,
  PrismMessage,
  PrismTable,
  PrismTooltip,
} from '@mylighthouse/prism-ember';
import type { IconName } from '@mylighthouse/prism-ember/types/components/prism/icon';
import type {
  CustomEmberTableColumn,
  CustomEmberTableRow,
} from '@mylighthouse/prism-ember/types/components/prism/table';
import { task } from 'ember-concurrency';
import { t } from 'ember-intl';
import { or } from 'ember-truth-helpers';
import SettingsCurrencyConversionDropdownMoreActions from 'lumen/components/settings/currency-conversion/dropdowns/more-actions';
import SettingsCurrencyConversionMultipleSelectionCurrencyPicker from 'lumen/components/settings/currency-conversion/multiple-selection-currency-picker';
import SettingsCurrencyConversionTablesCellsTdNumber from 'lumen/components/settings/currency-conversion/tables/cells/td-number';
import SettingsPageTemplate from 'lumen/components/settings/page-template';
import type {
  Currency,
  CurrencyConversionExchangeRatesProfile,
} from 'lumen/models';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';

export const PORTFOLIO_ACCESS = {
  EDIT: 'edit',
  VIEW: 'view',
} as const;

export type PortfolioAccess =
  (typeof PORTFOLIO_ACCESS)[keyof typeof PORTFOLIO_ACCESS];

interface SettingsCurrencyConversionProfileSignature {
  Args: {
    baseCurrencies: Currency[];
    exchangeRatesProfile: CurrencyConversionExchangeRatesProfile | null;
    isLoading?: boolean;
    isReadOnly?: boolean;
    onNavigateToPortfolio?: () => void;
    portfolioAccess?: PortfolioAccess | null;
  };
}

export default class SettingsCurrencyConversionProfile extends Component<SettingsCurrencyConversionProfileSignature> {
  @service declare currencyConversion: Services['currency-conversion'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare intl: Services['intl'];
  @service declare toast: Services['toast'];
  @service declare usageAnalytics: Services['usage-analytics'];

  @tracked hasAttemptedSave = false;
  @tracked isBannerDismissed = false;

  @tracked isCurrencyPickerOpen = false;

  saveProfileTask = task(async (): Promise<void> => {
    if (!this.args.exchangeRatesProfile) {
      return;
    }

    this.hasAttemptedSave = true;

    if (this.args.exchangeRatesProfile.hasInvalidRates) {
      return;
    }

    try {
      await this.currencyConversion.saveExchangeRatesProfile(
        this.args.exchangeRatesProfile,
      );

      this.hasAttemptedSave = false;
      this.toast.success(
        this.intl.t('settings.currency_conversion.changes_saved'),
        {
          showIcon: true,
        },
      );

      this.usageAnalytics.track('Exchange Rate Saved', {
        eventTrigger: 'profile-edited',
      });
    } catch {
      this.toast.error(this.intl.t('general.error_short'), { showIcon: true });
    }
  });

  // "Portfolio managed" takes priority over "View only" — a hotel with spiderMarketId is always read-only
  // and managed at portfolio level, so showing "Portfolio managed" is more informative than "View only".
  get badgeText(): string | undefined {
    if (this.args.portfolioAccess) {
      return this.intl.t('settings.currency_conversion.portfolio_managed');
    }
    if (this.args.isReadOnly) {
      return this.intl.t('settings.currency_conversion.view_only');
    }
    return undefined;
  }

  get baseCurrency(): Currency | null {
    return this.args.exchangeRatesProfile?.baseCurrency ?? null;
  }

  get breadcrumbBackLink(): string {
    return this.currencyConversion.isHotelLevel
      ? 'hotel.settings.currency-conversion.index'
      : 'market.settings.currency-conversion.index';
  }

  get columns(): CustomEmberTableColumn[] {
    return [
      {
        name: this.intl.t('general.currency'),
        valuePath: 'currency',
        textAlign: 'left' as const,
        isReadOnly: this.args.isReadOnly ?? false,
      },
      {
        name: this.intl.t('general.exchange_rate'),
        valuePath: 'rate',
        textAlign: 'left' as const,
        locale: this.hotelAndMarketService.user.locale,
        isReadOnly: this.args.isReadOnly ?? false,
        tdComponent: SettingsCurrencyConversionTablesCellsTdNumber,
      },
    ];
  }

  get displayBaseCurrency(): string {
    if (!this.baseCurrency) {
      return '';
    }
    return `1 ${this.baseCurrency.code} (${this.baseCurrency.symbol})`;
  }

  get isSaveDisabled(): boolean {
    if (this.saveProfileTask.isRunning || !this.args.exchangeRatesProfile) {
      return true;
    }

    if (
      this.hasAttemptedSave &&
      this.args.exchangeRatesProfile.hasInvalidRates
    ) {
      return true;
    }

    return !this.args.exchangeRatesProfile.hasUnsavedChanges;
  }

  get pageTitle(): string {
    return this.args.exchangeRatesProfile?.title ?? '';
  }

  get portfolioButtonIconBefore(): IconName | undefined {
    return this.args.portfolioAccess === PORTFOLIO_ACCESS.EDIT
      ? 'edit-pencil'
      : undefined;
  }

  get portfolioButtonLabel(): string | undefined {
    if (!this.args.portfolioAccess) {
      return undefined;
    }
    return this.args.portfolioAccess === PORTFOLIO_ACCESS.EDIT
      ? this.intl.t('settings.currency_conversion.navigate_to_portfolio.edit')
      : this.intl.t('settings.currency_conversion.navigate_to_portfolio.view');
  }

  get portfolioButtonType(): 'primary' | 'secondary' {
    return this.args.portfolioAccess === PORTFOLIO_ACCESS.EDIT
      ? 'primary'
      : 'secondary';
  }

  get portfolioCurrencies(): Currency[] {
    return this.args.baseCurrencies;
  }

  get portfolioCurrencyCodes(): string[] {
    return this.args.baseCurrencies.map((currency) => currency.code);
  }

  get rows(): CustomEmberTableRow[] {
    return this.selectedProfileCurrencyCodes.map((currencyCode) => ({
      currency: currencyCode,
      rate: this.resolveDisplayRate(currencyCode),
      hasUnsavedChanges: this.resolveHasUnsavedChanges(currencyCode),
      updateInputValue: this.updateRateValue.bind(this, currencyCode),
    }));
  }

  get selectedProfileCurrencyCodes(): string[] {
    const currencySet = new Set<string>();

    // Add profile currencies (excluding deleted ones)
    this.args.exchangeRatesProfile?.rates.forEach((rate) => {
      if (!rate.isDeleted) {
        currencySet.add(rate.currencyCode);
      }
    });
    if (!this.args.isReadOnly) {
      // If user has write access, include portfolio currencies so that they can add the rates for them.
      this.portfolioCurrencyCodes.forEach((code) => {
        currencySet.add(code);
      });
    }

    if (this.baseCurrency) {
      currencySet.delete(this.baseCurrency.code);
    }

    return Array.from(currencySet);
  }

  get shouldShowBanner(): boolean {
    if (this.args.isReadOnly || this.isBannerDismissed) {
      return false;
    }

    return (
      !!this.args.exchangeRatesProfile?.isMissingPortfolioCurrencies &&
      !this.args.exchangeRatesProfile?.hasNoExchangeRates
    );
  }

  get shouldShowInvalidRatesWarning(): boolean {
    if (!this.hasAttemptedSave) {
      return false;
    }
    return this.args.exchangeRatesProfile?.hasInvalidRates ?? false;
  }

  /**
   * For hotel-level profiles, if a rate is missing for a currency, we want to show "--".
   * For market-level profiles, we resolve it as is.
   */
  resolveDisplayRate(currencyCode: string): number | string | null {
    const savedRate =
      this.args.exchangeRatesProfile?.rates.find(
        (rate) => rate.currencyCode === currencyCode,
      )?.value ?? null;

    return this.currencyConversion.isHotelLevel
      ? (savedRate ?? this.intl.t('general.missing'))
      : savedRate;
  }

  /**
   * Determines if a rate has unsaved changes for a given currency.
   */
  resolveHasUnsavedChanges(currencyCode: string): boolean {
    const rate = this.args.exchangeRatesProfile?.rates.find(
      (rate) => rate.currencyCode === currencyCode,
    );

    return rate ? rate.hasDirtyAttributes || rate.isNew : false;
  }

  @action
  dismissBanner(): void {
    this.isBannerDismissed = true;
  }

  @action
  handleCurrencySelection(currencyCodes: string[]): void {
    if (!this.args.exchangeRatesProfile) {
      return;
    }

    this.currencyConversion.handleCurrencySelection(
      currencyCodes,
      this.args.exchangeRatesProfile,
    );
  }

  @action
  setCurrencyPickerIsOpen(value: boolean): void {
    this.isCurrencyPickerOpen = value;
  }

  @action
  updateRateValue(currencyCode: string, newValue: number | null): void {
    const profileRate = this.args.exchangeRatesProfile?.rates.find(
      (rate) => rate.currencyCode === currencyCode,
    );

    if (profileRate) {
      profileRate.value = newValue;
    }
  }

  <template>
    <SettingsPageTemplate
      @badge={{this.badgeText}}
      @isFullHeight={{true}}
      @title={{this.pageTitle}}
      @type="container"
    >
      <:breadcrumb>
        <PrismBreadcrumb as |B|>
          <B.item
            @label={{t "general.currency"}}
            @route={{this.breadcrumbBackLink}}
          />
          <B.item @isCurrent={{true}} @label={{this.pageTitle}} />
        </PrismBreadcrumb>
      </:breadcrumb>

      <:headerAfter>
        <div class="flex flex-row gap-200">
          {{#unless @isReadOnly}}
            {{#if @exchangeRatesProfile}}
              <SettingsCurrencyConversionDropdownMoreActions
                @exchangeRatesProfile={{@exchangeRatesProfile}}
              />
            {{/if}}

            <div>
              <PrismButton
                @isDisabled={{this.isSaveDisabled}}
                @isLoading={{or @isLoading this.saveProfileTask.isRunning}}
                @label={{t "settings.currency_conversion.save_changes"}}
                @onClick={{this.saveProfileTask.perform}}
                data-test-id="currency-conversion-save-button"
              />
              {{#unless this.isSaveDisabled}}
                <PrismTooltip
                  @side="top"
                  @text={{t
                    "settings.currency_conversion.save_changes_tooltip"
                  }}
                />
              {{/unless}}
            </div>
          {{/unless}}

          {{#if @portfolioAccess}}
            <PrismButton
              @buttonType={{this.portfolioButtonType}}
              @iconBefore={{this.portfolioButtonIconBefore}}
              @label={{this.portfolioButtonLabel}}
              @onClick={{@onNavigateToPortfolio}}
              data-test-id="currency-conversion-navigate-to-portfolio"
            />
          {{/if}}
        </div>
      </:headerAfter>

      <:default>
        <div class="mb-400 flex w-full items-center justify-between">
          <div>
            {{#unless @isReadOnly}}
              {{#if this.baseCurrency}}
                <SettingsCurrencyConversionMultipleSelectionCurrencyPicker
                  @baseCurrency={{this.baseCurrency}}
                  @isOpen={{this.isCurrencyPickerOpen}}
                  @onApply={{this.handleCurrencySelection}}
                  @portfolioCurrencyCodes={{this.portfolioCurrencyCodes}}
                  @selectedProfileCurrencyCodes={{this.selectedProfileCurrencyCodes}}
                  @setIsOpen={{this.setCurrencyPickerIsOpen}}
                />
              {{/if}}
            {{/unless}}
          </div>

          <div class="subdued-text flex items-center gap-100">
            <p class="text-300--regular">
              {{t "settings.currency_conversion.base_currency"}}:
              <span class="text-300--bold">
                {{this.displayBaseCurrency}}
              </span>
            </p>

            <div>
              <PrismTooltip
                @side="bottom"
                @text={{t "settings.currency_conversion.base_currency_tooltip"}}
              />
              <PrismIcon
                @color="inherit"
                @name="circle-information-outline"
                @size="small"
              />
            </div>
          </div>

        </div>

        {{#if this.shouldShowBanner}}
          <div class="mb-400" data-test-currency-conversion-banner>
            <PrismBanner
              @bannerMessage={{t
                "settings.currency_conversion.missing_portfolio_currencies_banner"
              }}
              @bannerStyle="intelligence"
              @close={{this.dismissBanner}}
              @icon="artificial-intelligence"
            />
          </div>
        {{/if}}

        {{#if this.shouldShowInvalidRatesWarning}}
          <p
            class="text-200--regular text-critical-default mb-200 flex flex-row items-center gap-100"
            data-test-currency-conversion-invalid-rates-warning
          >
            <PrismIcon @color="critical" @name="circle-cross" @size="small" />
            {{t "settings.currency_conversion.alert.invalid_rates.text"}}
          </p>
        {{/if}}

        <PrismCard @contentLayout="noPadding" class="overflow-hidden">
          <:content>
            <PrismTable
              @columns={{this.columns}}
              @isLoading={{@isLoading}}
              @nrOfLoadingRows={{2}}
              @removeTableBorder={{true}}
              @rows={{this.rows}}
            >
              <:emptyState>
                <div class="flex w-full items-center justify-center">
                  <PrismMessage
                    @description={{t
                      "settings.currency_conversion.no_currencies_added_yet_description"
                    }}
                    @illustration="100x100_info"
                  >
                    <:primaryAction>
                      {{#unless @isReadOnly}}
                        <PrismButton
                          @buttonType="secondary"
                          @iconBefore="plus"
                          @label={{t
                            "settings.currency_conversion.add_currencies"
                          }}
                          @onClick={{fn this.setCurrencyPickerIsOpen true}}
                          data-test-id="currency-conversion-add-currencies-button"
                        />
                      {{/unless}}
                    </:primaryAction>
                  </PrismMessage>
                </div>
              </:emptyState>
            </PrismTable>
          </:content>
        </PrismCard>
      </:default>
    </SettingsPageTemplate>
  </template>
}
